import {
  APP_USER_ASSIGNMENT_RESOURCES,
  buildRbacUserSavePayload,
  serializeAssignedData
} from '../utils/assignedData'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { loadSessionField } from '../../auth/sessionProfileStore'
import { ArrowsLeftRight, PlusCircle, ShieldCheck, Tag } from '@phosphor-icons/react'
import UbiquaTowerToastAlert from '../../components/alerts/UbiquaTowerToastAlert'
import LoadingScreen from '../../components/LoadingScreen'
import FilterUbiquaMui from '../../components/Filters/FilterUbiquaMui/FilterUbiquaMui'
import { MUI_TABLE_PAGINATION_OPTIONS } from '../../components/MuiTablePagination'
import FormHeaderDetailActions from '../../components/action_button/FormHeaderDetailActions'
import {
  UI_COLOR_DANGER_ACTION,
  UI_COLOR_DANGER_ACTION_HOVER,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_HOVER
} from '../../constants/uiColors'
import { isAuthRedirectError } from '../../auth'
import { canManageRbacUsers } from '../../global_context/capabilities/rbacManagementCapabilities'
import RbacUsersTable from '../components/RbacUsersTable'
import {
  assignUserRoles,
  createRbacUser,
  fetchCurrentUser,
  fetchRbacUsers,
  updateRbacUser,
  updateRbacUserStatus,
  updateCanManageRbac
} from '../services/rbacApi'
import { fetchRoles } from '../roles/services/rolesApi'
import { getAssignableRbacRoles } from '../roles/shared/rbacRoleDetailHelpers'
import { getRbacIsAdmin, refreshRbacFromCurrentUser } from '../../auth/sessionRbac'
import { downloadRbacUsersCsv } from '../utils/exportRbacUsersCsv'
import { buildSelectValue, getSelectValues } from '../../components/Filters/filterSelectState'
import {
  buildRbacUserProfileUpdatePayload,
  haveRbacUserRolesChanged
} from './shared/rbacUserDetailHelpers'
import { RBAC_USER_DETAIL_TEXTS } from './shared/rbacUserDetailConstants'
import RbacUserConfirmDialog from '../components/RbacUserConfirmDialog'
import RbacUserEditDialog from '../components/RbacUserEditDialog'
import RbacBulkReassignRolesDialog from '../components/rbac-bulk-reassign-roles-dialog/RbacBulkReassignRolesDialog'
import RbacReassignUserDialog from '../components/RbacReassignUserDialog'
import '../../css/rbac/rbacAssignments.scss'

const EMPTY_CREATE_FORM = {
  name: '',
  email: '',
  phone: '',
  roleIds: []
}

const EMPTY_EDIT_FORM = {
  name: '',
  email: '',
  phone: '',
  roleIds: [],
  canManageRbac: false
}

const DEFAULT_RBAC_ROWS_PER_PAGE = 25
const RBAC_PHONE_MAX_DIGITS = 12
const RBAC_PHONE_MAX_LENGTH = 18
const USER_TOWER_FILTER_KEYS = {
  ROLE: 'role_ids',
  STATUS: 'status'
}
const GENERIC_ERROR_MESSAGE =
  'Ocurrió un error al procesar la solicitud. Revisa tu conexión e intenta de nuevo.'

function normalizeRbacPhoneValue(value = '') {
  const filteredValue = value.replace(/[^\d\s+\-()]/g, '')
  let digits = 0
  let normalizedValue = ''

  for (const char of filteredValue) {
    if (/\d/.test(char)) {
      if (digits >= RBAC_PHONE_MAX_DIGITS) continue
      digits += 1
    }

    if (normalizedValue.length >= RBAC_PHONE_MAX_LENGTH) break
    normalizedValue += char
  }

  return normalizedValue
}

export default function AssignRbacRolesPage() {
  const isAdmin = getRbacIsAdmin()
  const canCreateUser = canManageRbacUsers()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSearchInput = searchParams.get('q') || ''
  const initialRoleFilter = buildSelectValue(
    searchParams
      .get(USER_TOWER_FILTER_KEYS.ROLE)
      ?.split(',')
      .map((value) => value.trim())
      .filter(Boolean) || []
  )
  const initialStatusFilter = buildSelectValue(
    searchParams
      .get(USER_TOWER_FILTER_KEYS.STATUS)
      ?.split(',')
      .map((value) => value.trim())
      .filter(Boolean) || []
  )
  const initialPage = Math.max(Number(searchParams.get('page')) || 1, 1)
  const initialPerPage = MUI_TABLE_PAGINATION_OPTIONS.includes(Number(searchParams.get('per_page')))
    ? Number(searchParams.get('per_page'))
    : DEFAULT_RBAC_ROWS_PER_PAGE
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [filtersLoading, setFiltersLoading] = useState(true)
  const [exportingCsv, setExportingCsv] = useState(false)
  const [error, setError] = useState('')
  const [actionAlert, setActionAlert] = useState(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [saving, setSaving] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM)
  const [createErrors, setCreateErrors] = useState({})
  const [creating, setCreating] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editTarget, setEditTarget] = useState(null)
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM)
  const [editErrors, setEditErrors] = useState({})
  const [updatingUser, setUpdatingUser] = useState(false)
  const [modalAlert, setModalAlert] = useState(null)
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)
  const [statusTarget, setStatusTarget] = useState(null)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false)
  const [pagination, setPagination] = useState({
    current_page: initialPage,
    per_page: initialPerPage,
    total_count: 0,
    total_pages: 1
  })
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null)
  const [actionMenuUser, setActionMenuUser] = useState(null)
  const [searchInput, setSearchInput] = useState(initialSearchInput)
  const [appliedSearch, setAppliedSearch] = useState(initialSearchInput)
  const [appliedFilterSelections, setAppliedFilterSelections] = useState({
    [USER_TOWER_FILTER_KEYS.ROLE]: initialRoleFilter,
    [USER_TOWER_FILTER_KEYS.STATUS]: initialStatusFilter
  })

  const hasEditChanges = useMemo(() => {
    if (!editTarget) return false

    const profilePayload = buildRbacUserProfileUpdatePayload(editForm, editTarget)
    return (
      Object.keys(profilePayload).length > 0 ||
      editForm.assignedDataDirty ||
      haveRbacUserRolesChanged(editForm.roleIds, editTarget.role_ids) ||
      (isAdmin && !!editForm.canManageRbac !== !!editTarget.can_manage_rbac)
    )
  }, [editForm, editTarget, isAdmin])

  const canSubmitEdit =
    editForm.name.trim() &&
    editForm.email.trim() &&
    editForm.roleIds.length > 0 &&
    hasEditChanges &&
    !updatingUser

  const userTowerFilterConfigs = useMemo(
    () => [
      {
        key: USER_TOWER_FILTER_KEYS.ROLE,
        title: 'ROL ASIGNADO',
        icon: ShieldCheck,
        options: roles.map((role) => ({ value: String(role.id), label: role.name || '—' }))
      },
      {
        key: USER_TOWER_FILTER_KEYS.STATUS,
        title: 'ESTADO',
        icon: Tag,
        options: [
          { value: 'pending', label: 'PENDIENTE' },
          { value: 'active', label: 'ACTIVO' },
          { value: 'inactive', label: 'INACTIVO' }
        ]
      }
    ],
    [roles]
  )

  const assignableRoles = useMemo(() => getAssignableRbacRoles(roles), [roles])
  const editDialogRoles = useMemo(
    () => getAssignableRbacRoles(roles, editTarget?.roles),
    [editTarget?.roles, roles]
  )

  const loadFilterData = useCallback(async () => {
    try {
      const rolesData = await fetchRoles({ includeInactive: true })
      setRoles(rolesData)
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setError(GENERIC_ERROR_MESSAGE)
    } finally {
      setFiltersLoading(false)
    }
  }, [])

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const selectedRoleIds = getSelectValues(appliedFilterSelections[USER_TOWER_FILTER_KEYS.ROLE])
      const selectedStatuses = getSelectValues(
        appliedFilterSelections[USER_TOWER_FILTER_KEYS.STATUS]
      )
      const usersData = await fetchRbacUsers({
        search: appliedSearch.trim(),
        status: selectedStatuses,
        role_ids: selectedRoleIds,
        page: pagination.current_page,
        per_page: pagination.per_page
      })
      const nextUsers = Array.isArray(usersData) ? usersData : usersData.users || []
      setUsers(nextUsers)
      if (usersData.pagination) {
        const totalCount = usersData.pagination.total || 0
        setPagination((prevPagination) => ({
          ...prevPagination,
          server: true,
          current_page: usersData.pagination.page || prevPagination.current_page,
          per_page: usersData.pagination.per_page || prevPagination.per_page,
          total_count: totalCount,
          total_pages: Math.max(
            Math.ceil(totalCount / (usersData.pagination.per_page || prevPagination.per_page)),
            1
          )
        }))
      }
      setLoading(false)
      setHasLoaded(true)
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setError(GENERIC_ERROR_MESSAGE)
      setLoading(false)
      setHasLoaded(true)
    }
  }, [appliedFilterSelections, appliedSearch, pagination.current_page, pagination.per_page])

  useEffect(() => {
    loadFilterData()
  }, [loadFilterData])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    const nextParams = new URLSearchParams()
    const trimmedSearch = appliedSearch.trim()
    const roleValues = getSelectValues(appliedFilterSelections[USER_TOWER_FILTER_KEYS.ROLE])
    const statusValues = getSelectValues(appliedFilterSelections[USER_TOWER_FILTER_KEYS.STATUS])

    if (trimmedSearch) nextParams.set('q', trimmedSearch)
    if (roleValues.length > 0) nextParams.set(USER_TOWER_FILTER_KEYS.ROLE, roleValues.join(','))
    if (statusValues.length > 0)
      nextParams.set(USER_TOWER_FILTER_KEYS.STATUS, statusValues.join(','))
    if (pagination.current_page > 1) nextParams.set('page', String(pagination.current_page))
    if (pagination.per_page !== DEFAULT_RBAC_ROWS_PER_PAGE) {
      nextParams.set('per_page', String(pagination.per_page))
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true })
    }
  }, [
    appliedFilterSelections,
    appliedSearch,
    pagination.current_page,
    pagination.per_page,
    searchParams,
    setSearchParams
  ])

  const filteredUsers = useMemo(() => {
    return users
  }, [users])

  useEffect(() => {
    if (!hasLoaded || pagination.server) return

    setPagination((prevPagination) => {
      const totalCount = filteredUsers.length
      const totalPages = Math.max(Math.ceil(totalCount / prevPagination.per_page), 1)
      const currentPage = Math.min(prevPagination.current_page, totalPages)

      if (
        prevPagination.total_count === totalCount &&
        prevPagination.total_pages === totalPages &&
        prevPagination.current_page === currentPage
      ) {
        return prevPagination
      }

      return {
        ...prevPagination,
        total_count: totalCount,
        total_pages: totalPages,
        current_page: currentPage
      }
    })
  }, [filteredUsers.length, hasLoaded, pagination.server])

  const paginatedUsers = useMemo(() => {
    if (pagination.server) return filteredUsers

    const startIndex = (pagination.current_page - 1) * pagination.per_page
    return filteredUsers.slice(startIndex, startIndex + pagination.per_page)
  }, [filteredUsers, pagination.current_page, pagination.per_page, pagination.server])

  const isAnyFilterSelected = useMemo(
    () =>
      Object.values(appliedFilterSelections).some(
        (selection) => getSelectValues(selection).length > 0
      ) || Boolean(appliedSearch.trim()),
    [appliedFilterSelections, appliedSearch]
  )

  const clearHeaderAlert = useCallback(() => {
    setError('')
    setActionAlert(null)
  }, [])

  const closeToastAlert = useCallback(() => {
    setModalAlert(null)
    setError('')
    setActionAlert(null)
  }, [])

  const showActionAlert = useCallback((message, severity = 'success') => {
    setError('')
    setActionAlert({
      id: Date.now(),
      severity,
      message
    })
  }, [])

  const handleMuiPageChange = (_event, nextPage) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: nextPage
    }))
  }

  const handleFilterChange = (filterKey, nextValue) => {
    setAppliedFilterSelections((prevSelections) => ({
      ...prevSelections,
      [filterKey]: nextValue
    }))
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: 1
    }))
  }

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value)
  }

  const handleSearchEnter = (event) => {
    if (event.key !== 'Enter') return
    event.preventDefault()
    setAppliedSearch(searchInput)
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: 1
    }))
  }

  const handleSearchClear = () => {
    setSearchInput('')
    setAppliedSearch('')
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: 1
    }))
  }

  const handleResetFilters = () => {
    setAppliedFilterSelections({
      [USER_TOWER_FILTER_KEYS.ROLE]: buildSelectValue(),
      [USER_TOWER_FILTER_KEYS.STATUS]: buildSelectValue()
    })
    setSearchInput('')
    setAppliedSearch('')
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: 1
    }))
  }

  const handleExportCsv = async () => {
    if (exportingCsv) return
    setExportingCsv(true)
    try {
      const filters = {
        search: appliedSearch.trim(),
        status: getSelectValues(appliedFilterSelections[USER_TOWER_FILTER_KEYS.STATUS]),
        role_ids: getSelectValues(appliedFilterSelections[USER_TOWER_FILTER_KEYS.ROLE]),
        per_page: 100
      }
      const allUsers = []
      let page = 1
      let total = Infinity

      while (allUsers.length < total) {
        const result = await fetchRbacUsers({ ...filters, page })
        const batch = Array.isArray(result) ? result : result.users || []
        if (!batch.length) break
        allUsers.push(...batch)
        total = result.pagination?.total ?? allUsers.length
        page += 1
      }

      downloadRbacUsersCsv(allUsers)
    } catch (err) {
      if (!isAuthRedirectError(err)) {
        setError(GENERIC_ERROR_MESSAGE)
      }
    } finally {
      setExportingCsv(false)
    }
  }

  const handleMuiRowsPerPageChange = (_event, nextRowsPerPage) => {
    const safeRowsPerPage = MUI_TABLE_PAGINATION_OPTIONS.includes(nextRowsPerPage)
      ? nextRowsPerPage
      : DEFAULT_RBAC_ROWS_PER_PAGE

    setPagination((prevPagination) => ({
      ...prevPagination,
      per_page: safeRowsPerPage,
      current_page: 1,
      total_pages: Math.max(Math.ceil((prevPagination.total_count || 0) / safeRowsPerPage), 1)
    }))
  }

  const handleUserRowClick = (userId) => {
    navigate(`/configuracion/rbac/usuarios/${userId}`, {
      state: {
        from: `${location.pathname}${location.search}`
      }
    })
  }

  const openActionMenu = (event, user) => {
    event.stopPropagation()
    clearHeaderAlert()
    setActionMenuAnchorEl(event.currentTarget)
    setActionMenuUser(user)
  }

  const closeActionMenu = () => {
    setActionMenuAnchorEl(null)
  }

  const handleActionMenuExited = () => {
    setActionMenuUser(null)
  }

  const openAssignDialog = (user) => {
    clearHeaderAlert()
    setSelectedUser(user)
    setDialogOpen(true)
  }

  const closeDialog = () => {
    setDialogOpen(false)
  }

  const handleReassignDialogExited = () => {
    setSelectedUser(null)
  }

  const handleReassignUser = async (roleId) => {
    if (!selectedUser || !roleId) return

    try {
      setSaving(true)
      setError('')
      await assignUserRoles(selectedUser.id, [roleId])

      const currentUserId = loadSessionField('id')
      if (String(currentUserId) === String(selectedUser.id)) {
        await refreshRbacFromCurrentUser(fetchCurrentUser)
      }

      await loadData()
      showActionAlert(`El rol del usuario se actualizó correctamente. ${selectedUser.name}`)
      closeDialog()
    } catch (err) {
      if (isAuthRedirectError(err)) return
      closeDialog()
      setError(GENERIC_ERROR_MESSAGE)
    } finally {
      setSaving(false)
    }
  }

  const openEditDialog = (user) => {
    clearHeaderAlert()
    setEditTarget(user)
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      roleIds: user.role_ids || [],
      canManageRbac: !!user.can_manage_rbac
    })
    setEditErrors({})
    setModalAlert(null)
    setEditDialogOpen(true)
  }

  const openStatusDialog = (user) => {
    clearHeaderAlert()
    setStatusTarget(user)
    setStatusDialogOpen(true)
  }

  const handleEditAction = () => {
    const user = actionMenuUser
    closeActionMenu()
    if (user) openEditDialog(user)
  }

  const handleAssignAction = () => {
    const user = actionMenuUser
    closeActionMenu()
    if (user) openAssignDialog(user)
  }

  const handleStatusAction = () => {
    const user = actionMenuUser
    closeActionMenu()
    if (user) openStatusDialog(user)
  }

  const closeEditDialog = () => {
    setEditDialogOpen(false)
  }

  const handleEditDialogExited = () => {
    setEditTarget(null)
    setEditForm(EMPTY_EDIT_FORM)
    setEditErrors({})
    setModalAlert(null)
  }

  const closeStatusDialog = () => {
    setStatusDialogOpen(false)
  }

  const handleStatusDialogExited = () => {
    setStatusTarget(null)
  }

  const updateEditField = (field, value) => {
    const nextValue = field === 'phone' ? normalizeRbacPhoneValue(value) : value
    setEditForm((prev) => ({ ...prev, [field]: nextValue }))
    setEditErrors((prev) => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const validateEditForm = () => {
    const nextErrors = {}
    if (!editForm.name.trim()) nextErrors.name = 'Campo obligatorio'
    if (!editForm.email.trim()) nextErrors.email = 'Campo obligatorio'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email.trim())) {
      nextErrors.email = 'Ingrese un correo electrónico válido'
    }
    if (editForm.roleIds.length === 0) nextErrors.roleIds = 'Escoja una opción'
    setEditErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleUpdateUser = async () => {
    if (!editTarget || !validateEditForm()) return

    try {
      setUpdatingUser(true)
      setError('')
      setModalAlert(null)
      const profilePayload = buildRbacUserProfileUpdatePayload(editForm, editTarget)
      let updated = await updateRbacUser(
        editTarget.id,
        buildRbacUserSavePayload(editForm, profilePayload, APP_USER_ASSIGNMENT_RESOURCES)
      )
      if (isAdmin && !!editForm.canManageRbac !== !!editTarget.can_manage_rbac) {
        updated = await updateCanManageRbac(editTarget.id, editForm.canManageRbac)
      }
      await loadData()
      showActionAlert(`Usuario ${updated.name} actualizado correctamente.`)
      closeEditDialog()
    } catch (err) {
      if (isAuthRedirectError(err)) return
      setModalAlert({
        severity: 'error',
        title: 'No se pudo actualizar el usuario',
        message:
          err.message === 'Failed to fetch'
            ? GENERIC_ERROR_MESSAGE
            : err.message || GENERIC_ERROR_MESSAGE
      })
    } finally {
      setUpdatingUser(false)
    }
  }

  const handleUpdateUserStatus = async () => {
    if (!statusTarget) return

    try {
      setUpdatingStatus(true)
      setError('')
      const willActivate = statusTarget.status === 'inactive'
      const updated = await updateRbacUserStatus(statusTarget.id, {
        active: willActivate,
        reason: willActivate ? null : 'admin'
      })
      await loadData()
      showActionAlert(
        willActivate
          ? `El usuario ${updated.name} se activó correctamente. Ya puede iniciar sesión en Torre de Control.`
          : `El usuario ${updated.name} se inactivó correctamente.`
      )
      closeStatusDialog()
    } catch (err) {
      if (isAuthRedirectError(err)) return
      closeStatusDialog()
      setError(GENERIC_ERROR_MESSAGE)
    } finally {
      setUpdatingStatus(false)
    }
  }

  const openCreateDialog = () => {
    clearHeaderAlert()
    setCreateForm(EMPTY_CREATE_FORM)
    setCreateErrors({})
    setModalAlert(null)
    setCreateDialogOpen(true)
  }

  const openBulkDialog = () => {
    clearHeaderAlert()
    setBulkDialogOpen(true)
  }

  const closeCreateDialog = () => {
    setCreateDialogOpen(false)
  }

  const handleCreateDialogExited = () => {
    setCreateForm(EMPTY_CREATE_FORM)
    setCreateErrors({})
    setModalAlert(null)
  }

  const handleBulkReassignSuccess = async (userIds = []) => {
    setBulkDialogOpen(false)
    const currentUserId = loadSessionField('id')
    if (userIds.some((userId) => String(userId) === String(currentUserId))) {
      try {
        await refreshRbacFromCurrentUser(fetchCurrentUser)
      } catch (_err) {
        // The current user session may already have been revoked.
      }
    }
    await loadData()
    showActionAlert('Los usuarios se reasignaron correctamente')
  }

  const updateCreateField = (field, value) => {
    const nextValue = field === 'phone' ? normalizeRbacPhoneValue(value) : value
    setCreateForm((prev) => ({ ...prev, [field]: nextValue }))
    setCreateErrors((prev) => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const validateCreateForm = () => {
    const nextErrors = {}
    if (!createForm.name.trim()) nextErrors.name = 'Campo obligatorio'
    if (!createForm.email.trim()) nextErrors.email = 'Campo obligatorio'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.email.trim())) {
      nextErrors.email = 'Ingrese un correo electrónico válido'
    }
    if (createForm.roleIds.length === 0) nextErrors.roleIds = 'Escoja una opción'
    setCreateErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const isCreateEmailTakenError = (message = '') => {
    const normalizedMessage = message.toLowerCase()
    return (
      normalizedMessage.includes('email has already been taken') ||
      normalizedMessage.includes('has already been taken') ||
      normalizedMessage.includes('correo electrónico ingresado ya existe')
    )
  }

  const handleCreateUser = async () => {
    if (!validateCreateForm()) return
    try {
      setCreating(true)
      setError('')
      setModalAlert(null)
      const created = await createRbacUser({
        name: createForm.name.trim(),
        email: createForm.email.trim(),
        phone: createForm.phone.trim() || null,
        role_ids: createForm.roleIds,
        assigned_data: serializeAssignedData(createForm.assignedData, APP_USER_ASSIGNMENT_RESOURCES)
      })
      await loadData()
      showActionAlert(
        'El usuario se agregó correctamente. Le enviamos un correo para que active su cuenta.'
      )
      closeCreateDialog()
    } catch (err) {
      if (isAuthRedirectError(err)) return
      if (err.payload?.pending && err.payload?.user_id) {
        closeCreateDialog()
        await loadData()
        showActionAlert(err.message, 'warning')
        return
      }
      if (isCreateEmailTakenError(err.message)) {
        setCreateErrors((prev) => ({
          ...prev,
          email: 'Ya existe un usuario con este correo.'
        }))
        return
      }
      setModalAlert({
        severity: 'error',
        title: 'No se pudo crear el usuario',
        message: err.message || GENERIC_ERROR_MESSAGE
      })
    } finally {
      setCreating(false)
    }
  }

  if ((loading && !hasLoaded) || filtersLoading) {
    return <LoadingScreen text={RBAC_USER_DETAIL_TEXTS.loading} />
  }

  const hasUsers = (pagination.total_count || 0) > 0 || users.length > 0
  const filtersDisabled = !hasUsers && !isAnyFilterSelected
  const anyPageDialogOpen =
    dialogOpen || createDialogOpen || editDialogOpen || statusDialogOpen || bulkDialogOpen
  const toastAlert =
    (createDialogOpen || editDialogOpen) && modalAlert
      ? modalAlert
      : anyPageDialogOpen
        ? null
        : error
          ? {
              id: `rbac-users-error-${error}`,
              severity: 'error',
              message: error
            }
          : actionAlert

  return (
    <div className="rbac-users-page app-list-shell ubiqua-type">
      <UbiquaTowerToastAlert
        key={toastAlert?.id ?? toastAlert?.message}
        open={Boolean(toastAlert)}
        id={toastAlert?.id}
        severity={toastAlert?.severity}
        title={toastAlert?.title}
        message={toastAlert?.message}
        onClose={closeToastAlert}
      />
      <div className="index-header detailed-container index-header--action-bar">
        <div className="app-page-heading">
          <h2>Usuarios Torre</h2>
          <p>Administra los usuarios, sus roles y los datos a los que pueden acceder.</p>
        </div>
        {canCreateUser && (
          <div className="index-buttons create-action-btn-group">
            <FormHeaderDetailActions
              className="form-header-actions--modern form-header-actions--compact-gap"
              secondaryLabel="Reasignar roles"
              secondaryIcon={ArrowsLeftRight}
              secondaryVariant="secondary"
              secondaryButtonClassName="form-header-action-btn--brand-secondary"
              onSecondary={openBulkDialog}
              primaryLabel="Crear usuario nuevo"
              primaryIcon={PlusCircle}
              iconSize={18}
              stackOnMobile
              onPrimary={openCreateDialog}
            />
          </div>
        )}
      </div>

      <div className="index-table app-list-content app-content-enter">
        <div className="app-list-filters">
          <FilterUbiquaMui
            title="Filtrar usuarios"
            searchPlaceholder="Buscar por nombre, código o correo…"
            filters={userTowerFilterConfigs}
            defaultInlineFilterCount={2}
            compactSearch
            appliedFilterSelections={appliedFilterSelections}
            isAnyFilterSelected={isAnyFilterSelected}
            searchInput={searchInput}
            disabled={filtersDisabled}
            onFilterChange={handleFilterChange}
            onApplyFilters={(selections) => {
              setAppliedFilterSelections(selections)
              setPagination((previous) => ({ ...previous, current_page: 1 }))
            }}
            onResetFilters={handleResetFilters}
            onSearchChange={handleSearchChange}
            onSearchEnter={handleSearchEnter}
            onSearchClear={handleSearchClear}
            onExport={handleExportCsv}
            exportDisabled={loading || exportingCsv || !hasUsers}
            exportTooltip={exportingCsv ? 'Preparando CSV…' : 'Descargar usuarios filtrados en CSV'}
          />
        </div>

        <RbacUsersTable
          loading={loading}
          paginatedUsers={paginatedUsers}
          pagination={pagination}
          isAdmin={isAdmin}
          actionMenuAnchorEl={actionMenuAnchorEl}
          actionMenuUser={actionMenuUser}
          onUserRowClick={handleUserRowClick}
          onOpenActionMenu={openActionMenu}
          onCloseActionMenu={closeActionMenu}
          onActionMenuExited={handleActionMenuExited}
          onEditAction={handleEditAction}
          onAssignAction={handleAssignAction}
          onStatusAction={handleStatusAction}
          onPageChange={handleMuiPageChange}
          onRowsPerPageChange={handleMuiRowsPerPageChange}
        />
      </div>

      <RbacReassignUserDialog
        open={dialogOpen}
        onClose={closeDialog}
        onExited={handleReassignDialogExited}
        onSubmit={handleReassignUser}
        user={selectedUser}
        roles={assignableRoles}
        submitting={saving}
      />

      <RbacUserConfirmDialog
        open={statusDialogOpen}
        onClose={closeStatusDialog}
        onExited={handleStatusDialogExited}
        onConfirm={handleUpdateUserStatus}
        user={statusTarget}
        title={
          statusTarget?.status === 'inactive'
            ? RBAC_USER_DETAIL_TEXTS.activateModalTitle
            : RBAC_USER_DETAIL_TEXTS.deactivateModalTitle
        }
        body={
          statusTarget?.status === 'inactive'
            ? RBAC_USER_DETAIL_TEXTS.activateModalBody
            : RBAC_USER_DETAIL_TEXTS.deactivateModalBody
        }
        confirmLabel={
          statusTarget?.status === 'inactive'
            ? RBAC_USER_DETAIL_TEXTS.activateUser
            : RBAC_USER_DETAIL_TEXTS.deactivateUser
        }
        confirmColor={
          statusTarget?.status === 'inactive' ? UI_COLOR_PRIMARY : UI_COLOR_DANGER_ACTION
        }
        confirmHoverColor={
          statusTarget?.status === 'inactive'
            ? UI_COLOR_PRIMARY_HOVER
            : UI_COLOR_DANGER_ACTION_HOVER
        }
        submitting={updatingStatus}
        loadingLabel="Guardando..."
      />

      <RbacUserEditDialog
        mode="create"
        open={createDialogOpen}
        onClose={closeCreateDialog}
        onExited={handleCreateDialogExited}
        onSubmit={handleCreateUser}
        onValidateBasicData={validateCreateForm}
        roles={assignableRoles}
        form={createForm}
        errors={createErrors}
        onFieldChange={updateCreateField}
        submitting={creating}
      />

      <RbacUserEditDialog
        open={editDialogOpen}
        onClose={closeEditDialog}
        onExited={handleEditDialogExited}
        onSubmit={handleUpdateUser}
        roles={editDialogRoles}
        editTarget={editTarget}
        form={editForm}
        errors={editErrors}
        onFieldChange={updateEditField}
        submitting={updatingUser}
        canSubmit={canSubmitEdit}
        showCanManageRbac={isAdmin}
      />

      <RbacBulkReassignRolesDialog
        open={bulkDialogOpen}
        onClose={() => setBulkDialogOpen(false)}
        onSuccess={handleBulkReassignSuccess}
        roles={assignableRoles}
      />
    </div>
  )
}
